/**
 * v0.9.4c — "แม่น้ำตอนล่าง / น้ำทะเลหนุน" card, from /api/lower-river. MEASURED water
 * levels only (ThaiWater · สสน.) + สทนช. announcement headings; no forecast of the next
 * high water (no licensed source yet). The tide-table link points to the official
 * กรมอุทกศาสตร์ page — its data are not used here.
 */
import { awayWord, nearWord } from "./place-word";
import { peaksText, trendText, type Peaks, type Trend6 } from "./tw-trend";
import { cachedFetch } from "./net-cache";
export interface LowerStation {
  sid: number; name: string; km: number; role: "mouth" | "near"; distKm: number | null;
  points: [string, number][]; latest: { t: string; v: number } | null; stale: boolean;
  bankM: number | null; diffBankM: number | null; trend: "up" | "down" | "flat" | null; max24: { t: string; v: number } | null;
  trend6?: Trend6; peaks?: Peaks; // v0.9.10 A (measured)
}
export interface LowerAlert { title: string; from: string; to: string; provinces: string[]; link: string }
export interface LowerRiverData {
  tidal: { river: string; dKm: number; userKm: number; stations: LowerStation[] } | null;
  alerts: LowerAlert[]; province: string | null; source: string; alertSource: string;
}

const HDRTN_TIDE_URL = "https://hydro.navy.mi.th/waterlaveltable";
const ONWR_NOTICES_URL = "https://www.onwr.go.th/?page_id=12310"; // สทนช. "ประกาศ" page
const THAI_MON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));
/** "2026-10-04T05:10+07:00" → "4 ต.ค. 05:10 น." (the API already gives Thai time) */
function thTime(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${Number(m[3])} ${THAI_MON[Number(m[2]) - 1]} ${m[4]}:${m[5]} น.` : iso;
}
function thDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${Number(m[3])} ${THAI_MON[Number(m[2]) - 1]} ${Number(m[1]) + 543}` : ymd;
}
const m2 = (v: number): string => v.toFixed(2);

export async function loadLowerRiver(lat: number, lon: number): Promise<LowerRiverData | null> {
  try {
    const r = await cachedFetch(`/api/lower-river?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`);
    if (!r.ok) return null;
    return (await r.json()) as LowerRiverData; // may hold neither (then no card is drawn)
  } catch { return null; }
}

/** 48-h line of the measured level, with the bank as a dashed line when known. */
function chart(s: LowerStation): string {
  const pts = s.points;
  if (pts.length < 2) return "";
  const W = 300, H = 74, P = 4;
  const t0 = Date.parse(pts[0]![0]), t1 = Date.parse(pts[pts.length - 1]![0]);
  const vals = pts.map((p) => p[1]).concat(s.bankM != null ? [s.bankM] : []);
  const lo = Math.min(...vals), hi = Math.max(...vals), span = hi - lo || 1;
  const x = (t: number): number => P + ((t - t0) / Math.max(1, t1 - t0)) * (W - 2 * P);
  const y = (v: number): number => H - P - ((v - lo) / span) * (H - 2 * P);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${x(Date.parse(p[0])).toFixed(1)},${y(p[1]).toFixed(1)}`).join("");
  const bank = s.bankM != null ? `<line x1="${P}" x2="${W - P}" y1="${y(s.bankM).toFixed(1)}" y2="${y(s.bankM).toFixed(1)}" class="lr-bank"/><text x="${W - P}" y="${(y(s.bankM) - 3).toFixed(1)}" class="lr-bank-t" text-anchor="end">ตลิ่ง ${m2(s.bankM)}</text>` : "";
  const mx = s.max24 ? `<circle cx="${x(Date.parse(s.max24.t)).toFixed(1)}" cy="${y(s.max24.v).toFixed(1)}" r="2.6" class="lr-max"/>` : "";
  return `<svg class="lr-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="ระดับน้ำ 48 ชั่วโมงที่ผ่านมา">${bank}<path d="${d}" class="lr-line"/>${mx}</svg>
    <div class="lr-axis muted small"><span>${esc(thTime(pts[0]![0]))}</span><span>ตอนนี้</span></div>`;
}

function stationBlock(s: LowerStation, opts: { hideFall: boolean }): string {
  const role = s.role === "mouth" ? "สถานีใกล้ปากแม่น้ำ" : `สถานี${nearWord()}ที่สุด`;
  const where = s.role === "near" && s.distKm != null ? ` · ${awayWord()} ~${s.distKm} กม.` : "";
  const lv = s.latest!;
  const bank = s.diffBankM == null ? `<span class="muted">ไม่มีข้อมูลระดับตลิ่งของสถานีนี้</span>`
    : s.diffBankM < 0 ? `ต่ำกว่าตลิ่ง ${m2(-s.diffBankM)} ม.` : s.diffBankM === 0 ? "เท่าระดับตลิ่ง" : `<b class="lr-over">สูงกว่าตลิ่ง ${m2(s.diffBankM)} ม.</b>`;
  // a fall is never shown in a flooded / ปภ.-declared area (it could read as "safe")
  // v0.9.10 A: 6-h measured rate (ซม./ชม.) — still never a fall in a flooded / declared area
  const trend = s.trend6 ? (s.trend6.kind === "down" && opts.hideFall ? "" : trendText(s.trend6) + " (6 ชม.)")
    : s.trend === "up" ? "▲ กำลังขึ้น" : s.trend === "down" ? (opts.hideFall ? "" : "▼ กำลังลง (ตามรอบน้ำ)") : s.trend === "flat" ? "ทรงตัว" : "";
  const peaks = peaksText(s.peaks);
  const maxVsBank = s.max24 && s.bankM != null
    ? (s.max24.v > s.bankM ? ` <b class="lr-over">(สูงกว่าตลิ่ง ${m2(s.max24.v - s.bankM)} ม.)</b>` : ` (ต่ำกว่าตลิ่ง ${m2(s.bankM - s.max24.v)} ม.)`) : "";
  const max = s.max24 ? `สูงสุดใน 24 ชม. ${m2(s.max24.v)} ม.รทก. เมื่อ ${esc(thTime(s.max24.t))}${maxVsBank}` : "";
  return `<div class="lr-st">
    <div class="lr-st-h"><b>${esc(s.name)}</b> <span class="muted small">${role} · ${Math.round(s.km)} กม. จากปากแม่น้ำ${where}</span></div>
    <div class="lr-now">ตอนนี้ <b>${m2(lv.v)} ม.รทก.</b> · ${bank}${trend ? ` · ${trend}` : ""}</div>
    ${max ? `<div class="muted small">${max}</div>` : ""}
    ${peaks ? `<div class="small lr-peaks">${esc(peaks)}</div>` : ""}
    ${chart(s)}
    <div class="muted small">ข้อมูล ณ ${esc(thTime(lv.t))}${s.stale ? " (อาจไม่เป็นปัจจุบัน)" : ""}</div>
  </div>`;
}

export function lowerRiverCard(d: LowerRiverData, opts: { hideFall: boolean }): HTMLElement {
  const card = document.createElement("div");
  card.className = "lr-card";
  const parts: string[] = [];
  if (d.tidal) {
    parts.push(`<div class="lr-head">🌊 แม่น้ำ${esc(d.tidal.river)}ตอนล่าง · น้ำทะเลหนุน</div>
      <div class="lr-lead">ช่วงนี้แม่น้ำ${esc(d.tidal.river)}ตอนล่างได้รับอิทธิพลน้ำทะเลหนุน ระดับน้ำขึ้นลงตามรอบน้ำทะเล</div>`);
    parts.push(d.tidal.stations.map((s) => stationBlock(s, opts)).join(""));
  } else {
    parts.push(`<div class="lr-head">🌊 น้ำทะเลหนุน</div>`);
  }
  for (const a of d.alerts) {
    parts.push(`<div class="lr-alert">📢 <b>${esc(a.title)}</b> · ${esc(thDay(a.from))} – ${esc(thDay(a.to))}
      <div class="small">จังหวัด: ${esc(a.provinces.join(" · "))}</div>
      <div class="muted small">ที่มา: ${esc(d.alertSource)} · <a href="${esc(a.link)}" target="_blank" rel="noopener">อ่านฉบับเต็ม ↗</a></div></div>`);
  }
  parts.push(`<div class="lr-foot">
    ${d.tidal ? `<div class="muted small">ที่มา: ${esc(d.source)}</div>` : ""}
    <a class="small" href="${ONWR_NOTICES_URL}" target="_blank" rel="noopener">ดูประกาศ สทนช. ↗</a>
    <a class="small" href="${HDRTN_TIDE_URL}" target="_blank" rel="noopener">ดูตารางน้ำขึ้นน้ำลง (กรมอุทกศาสตร์ กองทัพเรือ) ↗</a>
  </div>`);
  card.innerHTML = parts.join("");
  return card;
}
